/**
 * Minimal data-fetch hook. The gateway returns raw payloads (no envelope);
 * "no data yet" is expressed by null fields inside a 200 body (e.g.
 * `{state: null}`), which pages render as honest empty states themselves.
 * No cache library — the dashboard is small and reads are cheap.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export type QueryState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: T }

export function useQuery<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
): { state: QueryState<T>; refresh: () => void } {
  const [state, setState] = useState<QueryState<T>>({ kind: 'loading' })
  const [tick, setTick] = useState(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    setState({ kind: 'loading' })
    fetcher()
      .then((data) => {
        if (!alive.current) return
        setState({ kind: 'ready', data })
      })
      .catch((err: unknown) => {
        if (!alive.current) return
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        })
      })
    return () => {
      alive.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps])

  const refresh = useCallback(() => setTick((t) => t + 1), [])
  return { state, refresh }
}
